import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatINR } from "@/data/products";
import { LOYALTY, maxRedeemablePoints } from "@/data/site";

const emptyAddress = { label: "Home", fullName: "", phone: "", line1: "", line2: "", city: "", state: "", postalCode: "", country: "India" };

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const buildOrderItems = (detailed) =>
  detailed.map((item) =>
    item.kind === "box"
      ? {
          kind: "box",
          size: item.size,
          collagenQty: item.collagenQty,
          creatineQty: item.creatineQty,
          subscription: item.subscription,
          qty: item.qty,
        }
      : {
          kind: "single",
          productSlug: item.productSlug,
          qty: item.qty,
          subscription: item.subscription,
        }
  );

const Checkout = () => {
  const { detailed, total, clear } = useCart();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  const [addressId, setAddressId] = useState(user?.addresses?.find((a) => a.isDefault)?._id || user?.addresses?.[0]?._id || "");
  const [showAddressForm, setShowAddressForm] = useState((user?.addresses || []).length === 0);
  const [address, setAddress] = useState(emptyAddress);
  const [savingAddress, setSavingAddress] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [usePoints, setUsePoints] = useState(false);

  const redeemablePoints = maxRedeemablePoints(user?.loyaltyPoints || 0, total);
  const pointsDiscount = usePoints ? redeemablePoints * LOYALTY.pointValue : 0;
  const payable = total - pointsDiscount;

  const saveAddress = async (e) => {
    e.preventDefault();
    setSavingAddress(true);
    try {
      const { data } = await api.post("/users/me/addresses", address);
      await refresh();
      const newest = data[data.length - 1];
      setAddressId(newest._id);
      setShowAddressForm(false);
      setAddress(emptyAddress);
      toast.success("Address saved");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save address");
    } finally {
      setSavingAddress(false);
    }
  };

  const placeOrder = async () => {
    if (!addressId) {
      toast.error("Select or add a shipping address");
      return;
    }
    if (detailed.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    setPlacing(true);
    try {
      const { data: orderRes } = await api.post("/orders", {
        items: buildOrderItems(detailed),
        addressId,
        usePoints: usePoints && redeemablePoints > 0,
      });

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Could not load payment gateway. Check your connection and try again.");
        setPlacing(false);
        return;
      }

      const razorpay = new window.Razorpay({
        key: orderRes.keyId,
        amount: orderRes.amount,
        currency: orderRes.currency,
        order_id: orderRes.razorpayOrderId,
        name: "DNA — Daily Nutrition Aesthetics",
        description: `Order ${orderRes.orderNumber}`,
        prefill: { name: user.name, email: user.email, contact: user.phone || "" },
        handler: async (response) => {
          try {
            await api.post(`/orders/${orderRes.orderId}/verify`, response);
            clear();
            refresh();
            navigate(`/order/${orderRes.orderId}`);
          } catch {
            toast.error("Payment verification failed. Contact support if you were charged.");
          }
        },
        modal: {
          ondismiss: () => {
            toast.info("Payment cancelled — your order is saved, you can retry from your account.");
            setPlacing(false);
          },
        },
        theme: { color: "#C07C5F" },
      });

      razorpay.open();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not place order");
      setPlacing(false);
    }
  };

  return (
    <section className="max-w-4xl mx-auto px-6 py-32">
      <p className="font-serif-lux italic font-light text-4xl mb-12">Checkout.</p>

      <div className="grid md:grid-cols-2 gap-16">
        <div>
          <h2 className="text-[11px] tracking-[0.35em] uppercase text-[#5C5C5C] mb-6">Shipping Address</h2>

          <div className="flex flex-col gap-4 mb-6">
            {(user?.addresses || []).map((a) => (
              <label
                key={a._id}
                data-testid={`checkout-address-${a._id}`}
                className={`border p-5 flex gap-4 cursor-pointer transition-colors duration-300 ${addressId === a._id ? "border-[#C07C5F]" : "border-[#EBE8E3]"}`}
              >
                <input type="radio" name="address" checked={addressId === a._id} onChange={() => setAddressId(a._id)} className="mt-1" />
                <div className="text-sm font-light">
                  <p className="font-medium">{a.fullName}</p>
                  <p className="text-[#5C5C5C]">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</p>
                  <p className="text-[#5C5C5C]">{a.city}, {a.state} {a.postalCode}</p>
                  <p className="text-[#5C5C5C]">{a.phone}</p>
                </div>
              </label>
            ))}
          </div>

          {!showAddressForm && (
            <button
              onClick={() => setShowAddressForm(true)}
              data-testid="checkout-add-address-btn"
              className="text-[11px] tracking-[0.3em] uppercase text-[#C07C5F] hover:text-[#111111] transition-colors duration-300"
            >
              + Add a new address
            </button>
          )}

          {showAddressForm && (
            <form onSubmit={saveAddress} className="border border-[#EBE8E3] p-6 flex flex-col gap-4" data-testid="checkout-address-form">
              <input required placeholder="Full name" value={address.fullName} onChange={(e) => setAddress({ ...address, fullName: e.target.value })} className="border-b border-[#EBE8E3] py-2 outline-none focus:border-[#C07C5F] bg-transparent text-sm" />
              <input required placeholder="Phone" value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} className="border-b border-[#EBE8E3] py-2 outline-none focus:border-[#C07C5F] bg-transparent text-sm" />
              <input required placeholder="Address line 1" value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} className="border-b border-[#EBE8E3] py-2 outline-none focus:border-[#C07C5F] bg-transparent text-sm" />
              <input placeholder="Address line 2 (optional)" value={address.line2} onChange={(e) => setAddress({ ...address, line2: e.target.value })} className="border-b border-[#EBE8E3] py-2 outline-none focus:border-[#C07C5F] bg-transparent text-sm" />
              <div className="grid grid-cols-2 gap-4">
                <input required placeholder="City" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} className="border-b border-[#EBE8E3] py-2 outline-none focus:border-[#C07C5F] bg-transparent text-sm" />
                <input required placeholder="State" value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} className="border-b border-[#EBE8E3] py-2 outline-none focus:border-[#C07C5F] bg-transparent text-sm" />
              </div>
              <input required placeholder="Postal code" value={address.postalCode} onChange={(e) => setAddress({ ...address, postalCode: e.target.value })} className="border-b border-[#EBE8E3] py-2 outline-none focus:border-[#C07C5F] bg-transparent text-sm" />
              <button type="submit" disabled={savingAddress} data-testid="checkout-save-address-btn" className="mt-2 bg-[#111111] text-[#F9F8F6] rounded-full py-3 text-[11px] tracking-[0.3em] uppercase hover:bg-[#C07C5F] transition-colors duration-500 disabled:opacity-50">
                {savingAddress ? "Saving…" : "Save Address"}
              </button>
            </form>
          )}
        </div>

        <div>
          <h2 className="text-[11px] tracking-[0.35em] uppercase text-[#5C5C5C] mb-6">Order Summary</h2>
          <div className="flex flex-col gap-6 mb-8">
            {detailed.map((item) => (
              <div key={item.key} className="flex justify-between text-sm font-light">
                <span>{item.name} × {item.qty}</span>
                <span>{formatINR(item.price * item.qty)}</span>
              </div>
            ))}
          </div>
          {redeemablePoints > 0 && (
            <label className="flex items-start gap-3 border border-[#EBE8E3] p-5 mb-6 cursor-pointer" data-testid="checkout-use-points">
              <input type="checkbox" checked={usePoints} onChange={(e) => setUsePoints(e.target.checked)} className="mt-1" />
              <span className="text-sm font-light">
                Use my loyalty points
                <span className="block text-[11px] text-[#5C5C5C] mt-1">
                  Redeem {redeemablePoints} of your {user.loyaltyPoints} points for {formatINR(redeemablePoints * LOYALTY.pointValue)} off
                </span>
              </span>
            </label>
          )}
          {pointsDiscount > 0 && (
            <div className="flex justify-between text-sm font-light text-[#C07C5F] mb-4" data-testid="checkout-points-discount">
              <span>Loyalty points</span>
              <span>− {formatINR(pointsDiscount)}</span>
            </div>
          )}
          <div className="flex justify-between border-t border-[#EBE8E3] pt-6 mb-3">
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#5C5C5C]">Total</span>
            <span className="font-serif-lux text-2xl font-medium" data-testid="checkout-total">{formatINR(payable)}</span>
          </div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5C5C] mb-10" data-testid="checkout-points-earn">
            You'll earn {Math.floor(payable * LOYALTY.earnRate)} loyalty points on this order
          </p>
          <button
            onClick={placeOrder}
            disabled={placing || detailed.length === 0}
            data-testid="checkout-pay-btn"
            className="group w-full flex items-center justify-center gap-3 bg-[#111111] text-[#F9F8F6] rounded-full py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#C07C5F] transition-colors duration-500 disabled:opacity-50"
          >
            {placing ? "Processing…" : `Pay ${formatINR(payable)}`}
            <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1.5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Checkout;
